import api from '@/api'
import { getDateForDatePicker } from '@/util'

const SET_REPORT_LIST = 'SET_REPORT_LIST'
const SET_ANIMALFEED = 'SET_ANIMALFEED'
const SET_LOAD = 'SET_LOAD'
const SET_UPLOAD = 'SET_UPLOAD'
const SET_PLANFEED = 'SET_PLANFEED'
const SET_INOUT = 'SET_INOUT'

const SET_FILTERS = 'SET_FILTERS'
const RESET_FILTERS = 'RESET_FILTERS'
const RESET_FILTERS_CHART = 'RESET_FILTERS_CHART'

const SET_TOTAL_COUNT = 'SET_TOTAL_COUNT'

const getDefaultFilters = (timezone) => ({
  planned_on_before: getDateForDatePicker(timezone),
  planned_on_after: getDateForDatePicker(timezone),
  animal_type: [],
  group: [],
  ration: [],
  worker: [],
  loading: [],
  status: [],
})

const COMPARE_FILTERS = (filters, toFilters) => {
  for (const key in toFilters) {
    if (Array.isArray(toFilters[key])) {
      if ((filters[key] || []).length !== toFilters[key].length) {
        return false
      }
    } else {
      if (filters[key] !== toFilters[key]) {
        return false
      }
    }
  }
  return true
}

export default {
  namespaced: true,

  state () {
    return {
      reportsList: [],
      animalfeed: [],
      load: [],
      upload: [],
      planfeed: [],
      inout: [],
      filters: {},
      totalCount: 0,
      hasFilters: false
    }
  },

  getters: {
    reportsList: (state) => state.reportsList,
    animalfeed: (state) => state.animalfeed,
    load: (state) => state.load,
    upload: (state) => state.upload,
    planfeed: (state) => state.planfeed,
    inout: (state) => state.inout,
    filters: (state) => state.filters,
    hasFilters: (state) => state.hasFilters,
    totalCount: (state) => state.totalCount
  },

  mutations: {
    [SET_REPORT_LIST] (state, data) {
      state.reportsList = data
    },

    [SET_ANIMALFEED] (state, data) {
      state.animalfeed = data
    },

    [SET_LOAD] (state, data) {
      state.load = data
    },

    [SET_UPLOAD] (state, data) {
      state.upload = data
    },

    [SET_PLANFEED] (state, data) {
      state.planfeed = data
    },

    [SET_INOUT] (state, data) {
      state.inout = data
    },

    [SET_FILTERS] (state, { filters, timezone }) {
      // Compare with previously stored timezone
      if (state.timezone !== timezone) {
        // Timezone has changed → reset filters
        state.timezone = timezone
        state.hasFilters = false

        // Always reset date filters
        state.filters.planned_on_before = getDateForDatePicker(timezone)
        state.filters.planned_on_after = getDateForDatePicker(timezone)

        // Conditionally reset only if exists
        const keysToReset = ['animal_type', 'group', 'ration', 'worker', 'loading', 'status']
        keysToReset.forEach(key => {
          if (key in state.filters) {
            state.filters[key] = []
          }
        })

        // Keep farm from incoming filters
        state.filters.farm = filters.farm

        return
      }

      // Timezone is the same → set provided filters
      state.filters = filters
      state.hasFilters = !COMPARE_FILTERS(state.filters, getDefaultFilters(timezone))
    },

    [RESET_FILTERS] (state, timezone) {
      state.filters = {
        farm: state.filters.farm,
        planned_on_before: getDateForDatePicker(timezone),
        planned_on_after: getDateForDatePicker(timezone)
      }
      state.hasFilters = !COMPARE_FILTERS(state.filters, getDefaultFilters(timezone))
    },

    [RESET_FILTERS_CHART] (state, timezone) {
      state.filters = {
        farm: state.filters.farm,
        planned_on_before: null,
        planned_on_after: null,
        animal_type: [],
        group: []
      }
      state.hasFilters = !COMPARE_FILTERS(state.filters, getDefaultFilters(timezone))
    },

    [SET_TOTAL_COUNT] (state, count) {
      state.totalCount = count
    }
  },

  actions: {
    async getReportList ({ commit, state }, params = {}) {
      for (const key in params) {
        state.filters[key] = params[key]
      }
      const filters = { ...state.filters }
      const { data } = await api.reports.list(filters)
      commit(SET_REPORT_LIST, data)
    },

    async makeReport ({ state }, link, params = {}) {
      for (const key in params) {
        state.filters[key] = params[key]
      }
      const filters = { ...state.filters }

      const { data } = await api.reports.make(link, filters)
      return data
    },

    async getAnimalfeed ({ commit, state }, params = {}) {
      for (const key in params) {
        state.filters[key] = params[key]
      }
      const filters = { ...state.filters }
      const { data, count } = await api.reports.animalfeed.list(filters)
      commit(SET_ANIMALFEED, data)
      commit(SET_TOTAL_COUNT, count)
    },

    async getLoad ({ commit, state }, params = {}) {
      for (const key in params) {
        state.filters[key] = params[key]
      }
      const filters = { ...state.filters }
      const { data, count } = await api.reports.load.list(filters)
      commit(SET_LOAD, data || [])
      commit(SET_TOTAL_COUNT, count || 0)
      return { data, count }
    },

    async getUpload ({ commit, state }, params = {}) {
      for (const key in params) {
        state.filters[key] = params[key]
      }
      const filters = { ...state.filters }
      const { data, count } = await api.reports.upload.list(filters)
      commit(SET_UPLOAD, data)
      commit(SET_TOTAL_COUNT, count)
    },

    async getPlanfeed ({ commit, state }, params = {}) {
      for (const key in params) {
        state.filters[key] = params[key]
      }
      const filters = { ...state.filters }
      const { data, count } = await api.reports.planfeed.list(filters)
      commit(SET_PLANFEED, data)
      commit(SET_TOTAL_COUNT, count)
    },

    async getInout ({ commit, state }, params = {}) {
      for (const key in params) {
        state.filters[key] = params[key]
      }
      const filters = { ...state.filters }
      const { data } = await api.reports.inout.list(filters)
      commit(SET_INOUT, data)
    },

    async getInoutnew ({ commit, state }, params = {}) {
      for (const key in params) {
        state.filters[key] = params[key]
      }
      const filters = { ...state.filters }
      const { data } = await api.reports.inoutnew.list(filters)
      commit(SET_INOUT, data)
    },

    applyFilters ({ commit, rootGetters }, filters = {}) {
      const timezone = rootGetters['account/timezone']
      commit(SET_FILTERS, { filters, timezone })
    },

    applyTotal ({ commit }, count) {
      commit(SET_TOTAL_COUNT, count)
    },

    resetFilters ({ commit, rootGetters }) {
      const timezone = rootGetters['account/timezone']
      commit(RESET_FILTERS, timezone)
    },

    resetFiltersChart ({ commit, rootGetters }) {
      const timezone = rootGetters['account/timezone']
      commit(RESET_FILTERS_CHART, timezone)
    }
  }
}
